import { createContext, useContext } from "react";
import { capital, firstLetter, letters } from "@/lib/letters";

/**
 * Who is signed in. The places an account keeps render under a session;
 * the pages for anyone (Jobs, a posting, Pricing, Support, the email
 * choices, the legal pages) read it when there is one.
 */
export type Session = {
    userId: string;
    email: string | null;
    /** The person's first name when the sign-in knows it. */
    firstName: string | null;
    lastName: string | null;
    /**
     * When the account was made, ISO 8601, when the sign-in knows it. A
     * first sign-in from Google makes the account without the sign-up
     * sheet, so this is how the terms prompt tells a new account from an
     * old one (lib/consent `accountJustMade`).
     */
    createdAt?: string | null;
    signOut: () => void;
};

export const SessionContext = createContext<Session | null>(null);

export function useSession(): Session {
    const session = useContext(SessionContext);
    if (!session) {
        throw new Error("useSession must be used under a signed-in session");
    }
    return session;
}

/** The session when someone is signed in, or null: for the frame every page shares. */
export function useOptionalSession(): Session | null {
    return useContext(SessionContext);
}

/** Where the sign-in stands. */
export type AuthState =
    { status: "loading" } | { status: "signed-in"; session: Session } | { status: "signed-out" };

/** What a signed-out page can ask of the sign-in. */
export interface SignInActions {
    /** AuthKit's sign-in page. */
    signIn: () => void;
    /**
     * AuthKit's sign-up page. With an email, AuthKit opens with it filled
     * in, as a login hint; AuthKit sends any email link itself.
     */
    signUp: (email?: string) => void;
}

export interface Auth extends SignInActions {
    state: AuthState;
}

export const AuthContext = createContext<Auth>({
    state: { status: "loading" },
    signIn: () => {},
    signUp: () => {},
});

/** The sign-in, from anywhere under the provider. */
export function useAuth(): Auth {
    return useContext(AuthContext);
}

/**
 * What to call the person: the first name the sign-in gave, else the part
 * of the email before the @, else nothing. The greeting and the sidebar
 * read this.
 */
export function displayName(session: Pick<Session, "email" | "firstName">): string | null {
    const first = session.firstName?.trim();
    if (first) {
        return first;
    }
    const local = session.email?.split("@")[0]?.trim();
    if (!local) {
        return null;
    }
    // The first letter as a capital, whole: an emoji or an accent written
    // after its letter stays one letter, and `ß` does not become `SS`.
    const letter = firstLetter(local);
    return capital(letter) + local.slice(letter.length);
}

/** Two letters for the avatar: first and last name, or the first two of the name. */
export function initials(session: Pick<Session, "email" | "firstName" | "lastName">): string {
    // A letter is what a person sees, not a UTF-16 unit: an emoji is two
    // units, an accent may follow its letter, and half of one is a box.
    const first = firstLetter(session.firstName ?? "");
    const last = firstLetter(session.lastName ?? "");
    if (first && last) {
        return `${capital(first)}${capital(last)}`;
    }
    const name = displayName(session) ?? "";
    return letters(name, 2).map(capital).join("") || "JE";
}

/**
 * Where AuthKit keeps its refresh token in devMode (AuthProvider). AuthKit
 * keeps it under this key followed by `:<client id>`, and still reads the
 * bare key as an older fallback, so either counts.
 */
const AUTHKIT_REFRESH_TOKEN = "workos:refresh-token";

/** The parts of Storage the guess reads: a key by its place, and its value. */
type TokenStorage = Pick<Storage, "getItem" | "key" | "length">;

/**
 * Whether this browser holds a sign-in AuthKit can resume. A guess at
 * where the sign-in will land, for a page to draw the shape it will have
 * while the sign-in is read, so nothing moves when it lands. Never a
 * reason to show what an account keeps.
 */
export function likelySignedIn(
    storage: () => TokenStorage | undefined = () => window.localStorage,
): boolean {
    // Inside the try: reading localStorage itself throws where it is blocked.
    try {
        const store = storage();
        if (!store) {
            return false;
        }
        if (store.getItem(AUTHKIT_REFRESH_TOKEN)) {
            return true;
        }
        for (let index = 0; index < store.length; index++) {
            const key = store.key(index);
            if (key?.startsWith(`${AUTHKIT_REFRESH_TOKEN}:`) && store.getItem(key)) {
                return true;
            }
        }
        return false;
    } catch {
        return false;
    }
}
